import { jsPDF } from "jspdf";
import logoUrl from "../assets/WTR_logo.png";
import { getEducationalCaseViewerUrl } from "./educational-case-viewer-link";
import { getAssignmentThumbnailSource } from "./educational-assignment-thumbnail";

const MARGIN = 42;
const INK = [24, 62, 80];
const TEXT = [72, 83, 94];
const RULE = [207, 218, 224];
const WEBSITE = "https://www.worldtumorregistry.org";
const DISCLAIMER = "World Tumor Registry (WTR) tools and materials, including content provided in this document and linked educational resources, are intended solely for education. They do not constitute medical advice and must not be used for clinical diagnosis, treatment decisions, or patient management. They are not a substitute for independent professional judgment or consultation with a qualified healthcare professional.";

const displayValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") return value.name || value.label || fallback;
  return String(value);
};

const loadImage = async (source) => {
  if (!source) return null;
  if (source.startsWith("data:image/")) return source;
  const response = await fetch(source, { credentials: "omit" });
  if (!response.ok) throw new Error("Unable to load report image");
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const addProportionalImage = (doc, data, x, y, width, height) => {
  const image = doc.getImageProperties(data);
  const scale = Math.min(width / image.width, height / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  const bounds = [x + (width - w) / 2, y + (height - h) / 2, w, h];
  doc.addImage(data, ...bounds, undefined, "FAST");
  return bounds;
};

export const createEducationalAssignmentPdf = async (report) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  const participant = report.reportType === "participant" || report.includeCaseLinks === false;
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const bottom = height - 70;
  const contentWidth = width - MARGIN * 2;
  const textX = MARGIN + 108;
  const textWidth = width - MARGIN - textX;
  const assignmentId = displayValue(report.systemIdNumber, "assignment");
  const logo = await loadImage(logoUrl);
  let y;
  let pageStart;
  let continuationStart;

  const font = (size, bold = false, color = TEXT) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };
  const header = (continued = false) => {
    addProportionalImage(doc, logo, MARGIN, 16, 124, 32);
    font(21, true, INK);
    doc.text("Unknown Slides Assignment", MARGIN, 73);
    font(14, true, INK);
    doc.text(participant ? "Participant Responses and Comments" : "Author Answers and Comments", MARGIN, 95);
    font(11, true, INK);
    const idLines = doc.splitTextToSize(`Unknown Slides Assignment ID: ${assignmentId}`, contentWidth);
    doc.text(idLines, MARGIN, 122);
    y = 122 + idLines.length * 14;
    continuationStart = y + 16;
    if (!continued) {
      font(10.5);
      const date = report.reportDate ? `${participant ? "Submitted on" : "Generated on"}: ${report.reportDate}` : "";
      let dateOnParticipantLine = false;
      if (participant) {
        const lines = doc.splitTextToSize(`Participant: ${displayValue(report.user, "Not provided")}`, contentWidth);
        doc.text(lines, MARGIN, y + 5);
        if (date && lines.length === 1 && doc.getTextWidth(lines[0]) + doc.getTextWidth(date) + 30 < contentWidth) {
          doc.text(date, width - MARGIN, y + 5, { align: "right" });
          dateOnParticipantLine = true;
        }
        y += lines.length * 13;
      }
      if (date && !dateOnParticipantLine) {
        doc.text(date, MARGIN, y + 5);
        y += 16;
      }
    }
    y += 16;
    pageStart = y;
  };
  const newPage = () => { doc.addPage(); header(true); };
  const rule = (at) => { doc.setDrawColor(...RULE); doc.setLineWidth(0.5); doc.line(MARGIN, at, width - MARGIN, at); };
  header();

  // Measure the styled, wrapped lines before choosing a page for each case.
  const fieldLines = (label, value) => {
    font(10.5, true);
    const labelWidth = doc.getTextWidth(`${label} `);
    font(10.5);
    const lines = [];
    let first = true;
    for (const paragraph of value.replace(/\r\n?/g, "\n").split("\n")) {
      let remaining = paragraph;
      do {
        const available = textWidth - (first ? labelWidth : 0);
        const line = doc.splitTextToSize(remaining || " ", available)[0];
        lines.push({ text: line, label: first ? label : "", offset: first ? labelWidth : 0 });
        remaining = remaining.slice(Math.max(1, line.trimEnd().length)).replace(/^ +/, "");
        first = false;
      } while (remaining.length);
    }
    return lines;
  };

  const cases = Array.isArray(report.cases) ? report.cases : [];
  for (let index = 0; index < cases.length; index++) {
    const item = cases[index];
    const caseId = displayValue(item.caseSystemNumber || item.systemCaseNumber || item.caseNumber);
    const identifier = `Case ${index + 1}${!participant && caseId ? ` | ${caseId}` : ""}`;
    const caseUrl = !participant && report.includeCaseLinks !== false ? getEducationalCaseViewerUrl(item) : "";
    const diagnosis = [item.tumorSite, item.tumorType, item.tumorSubType].map(v => displayValue(v, "Unknown")).join(" - ");
    const comments = displayValue(item.comments).trim() ? displayValue(item.comments) : "No comments provided.";
    const lines = [
      ...fieldLines(participant ? "Submitted diagnosis:" : "Diagnosis:", diagnosis),
      ...fieldLines(participant ? "Participant comments:" : "Author comments:", comments),
    ];
    let thumbnail = null;
    try { thumbnail = await loadImage(getAssignmentThumbnailSource(item)); }
    catch (error) { console.warn("Unable to add assignment thumbnail to PDF", error); }

    let offset = 0;
    do {
      font(11.5, true, INK);
      const heading = doc.splitTextToSize(identifier + (offset ? " (continued)" : ""), textWidth);
      const headingHeight = heading.length * 14;
      const required = Math.max(82, 20 + headingHeight + (lines.length - offset) * 13);
      // Move a whole case to a fresh page before considering a continuation.
      if (y + required > bottom && (y > pageStart || required <= bottom - continuationStart)) newPage();
      const capacity = Math.max(1, Math.floor((bottom - y - 20 - headingHeight) / 13));
      const count = Math.min(lines.length - offset, capacity);
      const sectionHeight = Math.max(82, 20 + headingHeight + count * 13);
      rule(y);
      font(11.5, true, INK);
      doc.text(heading, textX, y + 17, { lineHeightFactor: 14 / 11.5 });
      if (caseUrl) doc.link(textX, y + 5, Math.min(textWidth, doc.getTextWidth(heading[0])), headingHeight, { url: caseUrl });
      if (thumbnail) {
        const bounds = addProportionalImage(doc, thumbnail, MARGIN, y + 10, 92, 62);
        if (caseUrl) doc.link(...bounds, { url: caseUrl });
      }
      for (let n = 0; n < count; n++) {
        const line = lines[offset + n];
        const baseline = y + 17 + headingHeight + n * 13;
        if (line.label) { font(10.5, true); doc.text(line.label, textX, baseline); }
        font(10.5);
        doc.text(line.text, textX + line.offset, baseline);
      }
      y += sectionHeight;
      offset += count;
      if (offset < lines.length) newPage();
    } while (offset < lines.length);
  }

  font(9);
  const disclaimerLines = doc.splitTextToSize(DISCLAIMER, contentWidth);
  if (y + 58 + disclaimerLines.length * 12 > bottom) newPage();
  rule(y + 5);
  font(11, true, INK);
  doc.text("For educational purposes only.", MARGIN, y + 25);
  font(9);
  doc.text(disclaimerLines, MARGIN, y + 45, { lineHeightFactor: 12 / 9 });
  y += 45 + disclaimerLines.length * 12;
  doc.textWithLink("www.worldtumorregistry.org", MARGIN, y + 12, { url: WEBSITE });
  if (report.link) {
    if (y + 32 > bottom) newPage();
    font(9);
    doc.textWithLink("View assignment", MARGIN, y + 28, { url: report.link });
  }
  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    rule(height - 59);
    font(8);
    doc.text(`${assignmentId} | For educational purposes only. Not for clinical diagnosis.`, MARGIN, height - 44);
    doc.text(`Page ${page} of ${pageCount}`, width - MARGIN, height - 44, { align: "right" });
  }
  const safeId = assignmentId.replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "") || "assignment";
  const bytes = new Uint8Array(doc.output("arraybuffer"));
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + 0x8000));
  return {
    pdfBase64: window.btoa(binary),
    pdfFileName: `WTR_Unknown_Slides_${safeId}_${participant ? "Participant_Responses" : "Answers"} and Comments.pdf`,
  };
};
